/* eslint-disable no-console */
import { createContext, useState, useEffect, useContext, useRef } from "react"; // Add useRef
import { toast } from "react-hot-toast";
import { ROLES } from "../constants/roles";
import { getUsers } from "../utils";
import { getCurrentUser } from "../utils";
import { useSessionContext } from "@supabase/auth-helpers-react";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { session, isLoading: authLoading, supabaseClient } = useSessionContext();
  const [apellido, setApellido] = useState(null);
  const [correo, setCorreo] = useState(null);
  const [edificiosOk, setEdificiosOk] = useState(null);
  const [habilitado, setHabilitado] = useState(null);
  const [id, setId] = useState(null);
  const [nombre, setNombre] = useState(null);
  const [pais, setPais] = useState(null);
  const [rol, setRol] = useState(null);
  const [, setPrevTokenVersion] = useState(0);
  const [userLoading, setUserLoading] = useState(false); // NUEVO: Para carga custom
  const loaded = useRef(false); // Ref to track if data has been loaded
  const prevSessionId = useRef(null); // Nuevo ref para trackear session ID

  const internalLogout = async () => {
    try {
      await supabaseClient.auth.signOut();
      window.location.href = "/login";
    } catch (error) {
      console.error("Error en internalLogout:", error);
    }
  };

  // Función de validación plain (usada en loadUserData con params temp)
  const validateUserData = async (userData, tempId, tempPrevTokenVersion) => {
    if (!userData) {
      toast.error("Datos de usuario no encontrados");
      return false;
    }
    if (!userData.habilitado) {
      toast.error("Usuario deshabilitado");
      return false;
    }
    if (userData.token_version > tempPrevTokenVersion) {
      toast.error("Sesión cerrada por administrador");
      return false;
    }
    if (!userData.rol || !Object.values(ROLES).includes(userData.rol)) {
      toast.error("Rol de usuario no válido o no autorizado");
      return false;
    }
    return true;
  };

  useEffect(() => {
    const getPais = (hostname) => {
      if (hostname.includes("visiona.pe")) return "Perú";
      else if (hostname.includes("wecast.cl")) return "Chile";
      else if (hostname.includes("screenmedia.com.uy")) return "Uruguay";
      else if (hostname.includes("adigitalholding.com")) return "Region";
      else return "Perú";
    };
    setPais(getPais(window.location.hostname));
  }, []);

  useEffect(() => {
    if (session && !loaded.current) {
      if (session.user?.id === prevSessionId.current) {
        console.log("Sesión no cambió, ignorando refresh"); // Log para debug
        return; // Ignora si es el mismo user ID (refresh fake)
      }
      loaded.current = true;
      const loadUserData = async () => {
        setUserLoading(true); // Inicia loading custom
        try {
          const user = await getCurrentUser();
          if (!user) throw new Error("No user data");
          let userData = user.permissions;
          if (!userData) {
            const users = await getUsers();
            console.log("getUsers result length:", users?.length);
            const matchedUser = users.find((u) => u.email && u.email.toLowerCase() === user.email.toLowerCase());
            console.log("getUsers matched?", !!matchedUser);
            if (!matchedUser) throw new Error("User not found in usuarios table");
            userData = matchedUser;
          }
          const tempId = user.id;
          const tempPrevTokenVersion = userData.token_version || 0;
          const valid = await validateUserData(userData, tempId, tempPrevTokenVersion);
          if (!valid) {
            await internalLogout();
            return;
          }
          // Setea states...
          setApellido(userData.apellido || null);
          setCorreo(user.email);
          setEdificiosOk(userData.edificiosOk || null);
          setHabilitado(userData.habilitado);
          setId(tempId);
          setNombre(userData.nombre || null);
          setRol(userData.rol);
          setPrevTokenVersion(tempPrevTokenVersion);
          prevSessionId.current = user.id; // Actualiza ref después de éxito
        } catch (error) {
          console.error("loadUserData error:", error.message);
          await internalLogout();
        } finally {
          setUserLoading(false); // Finaliza loading custom
          // NO resetear loaded.current aquí; solo si session null
        }
      };
      loadUserData();
    } else if (!session) {
      // Clear states on no session
      setApellido(null);
      setCorreo(null);
      setEdificiosOk(null);
      setHabilitado(null);
      setId(null);
      setNombre(null);
      setRol(null);
      setPrevTokenVersion(0);
      loaded.current = false; // Reset for next login
      prevSessionId.current = null;
    }
  }, [session]);

  const authContextValue = {
    apellido,
    correo,
    edificiosOk,
    habilitado,
    id,
    nombre,
    pais,
    rol,
    session,
    isLoading: authLoading || userLoading, // Updated: Now combines both loadings
    logs: [],
  };

  return <AuthContext.Provider value={authContextValue}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuthContext must be used within a AuthProvider");
  return context;
};
